import { Children, type ReactNode } from 'react';

import { Intent } from '../../../core/slots/Intent/Intent.js';
import { isSlot } from '../../../core/utils/is-slot.js';
import { iterateSparklineSlots } from '../../core/components/sparkline-display/utils/iterate-sparkline-slots.js';
import { ColorRule, isValidColorRule } from '../../core/slots/ColorRule.js';
import { sanitizeProps } from '../../core/slots/toolbar/CompactToolbar.js';
import { ColorRuleProps } from '../../core/types/color-rule.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import { Sparkline } from '../slots/Sparkline.js';
import { SingleValueGridToolbarSlot } from '../slots/Toolbar.js';
import { Trend } from '../slots/Trend.js';
import { Value } from '../slots/Value.js';
import {
  PartialSingleValueGridConfig,
  SingleValueGridAccessorsConfig,
  type SingleValueGridIntentsConfig,
  type SingleValueGridProps,
  type SingleValueGridToolbarConfig,
} from '../types/single-value-grid.js';

type SlotsConfig = PartialSingleValueGridConfig & {
  intents: SingleValueGridIntentsConfig;
  toolbar: SingleValueGridToolbarConfig;
};

export const iterateSlots = (
  children: ReactNode,
  props: Pick<SingleValueGridProps, 'alignment' | 'seriesActions'>,
): SlotsConfig & {
  accessors: SingleValueGridAccessorsConfig;
} => {
  const slots: SlotsConfig = {
    intents: { grid: [], single: undefined, none: true },
    toolbar: {
      hidden: false,
      placement: (props.alignment || '') === 'end' ? 'top-left' : 'top-right',
      'data-dt-content': '',
      'data-dt-name': '',
      'data-dt-children-name': '',
      'data-dt-features': '',
      'data-dt-properties': '',
      'data-dt-mouse-over': '',
      'data-dt-value-content': false,
      'data-dt-children-value-content': false,
      'data-dt-ancestor': '',
    },
  };
  const accessors: SingleValueGridAccessorsConfig = {};
  const colorRules: ColorRuleProps[] = [];
  Children.forEach(children, (child) => {
    if (isSlot(child, Value)) {
      const { dataAccessor, formatterAccessor, unitAccessor, ...valueProps } =
        child.props;

      slots.value = valueProps;

      accessors.value = {
        dataAccessor,
        formatterAccessor,
        unitAccessor,
      };
    } else if (isSlot(child, Sparkline)) {
      const {
        children: sparklineSlots,
        dataAccessor,
        colorAccessor,
        ...sparklineProps
      } = child.props;
      const { xAxis, yAxis } = iterateSparklineSlots(sparklineSlots);

      slots.sparkline = sparklineProps;

      accessors.sparkline = {
        dataAccessor,
        colorAccessor,
        xAxis,
        yAxis,
      };
    } else if (isSlot(child, Trend)) {
      const {
        directionAccessor,
        valueAccessor,
        labelAccessor,
        formatterAccessor,
        inverseTrendAccessor,
        ...trendProps
      } = child.props;

      slots.trend = trendProps;

      accessors.trend = {
        directionAccessor,
        valueAccessor,
        labelAccessor,
        formatterAccessor,
        inverseTrendAccessor,
      };
    } else if (isSlot(child, ColorRule)) {
      const colorRule = child.props;
      if (isValidColorRule(colorRule)) {
        colorRules.push(colorRule);
      }
    } else if (isSlot(child, Intent)) {
      slots.intents.grid.push(child.props);
      slots.intents.none = false;
    } else if (isSlot(child, SingleValueGridToolbarSlot)) {
      slots.toolbar = {
        ...slots.toolbar,
        ...sanitizeProps(child.props),
        ...extractBehaviorTrackingProps(child.props),
      };
    }
  });

  if (colorRules.length > 0) {
    slots.colorRules = colorRules;
  }

  if (slots.toolbar.hidden) {
    slots.intents = {
      ...slots.intents,
      grid: [],
      none: true,
    };
  } else {
    if (props.seriesActions) {
      slots.intents = {
        ...slots.intents,
        single: props.seriesActions,
        none: false,
      };
    }

    if (slots.intents.none) {
      slots.toolbar = {
        ...slots.toolbar,
        hidden: true,
      };
    }
  }

  return { ...slots, accessors };
};
